/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IActivity } from "../../../../interfaces/activities";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { debounce } from "lodash";
import {
  api,
  createActivity,
  deleteActivities,
  exportsXlsx,
  exportsXlsxCustom,
  listActivities,
  showActivity,
  updateActivity,
} from "@/services";
import { notifyDeleteResults } from "@/utils/deleteResults";
import { validateRequiredFields } from "./utils/validation";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";


const DEFAULT_DATA: IActivity = {
  id: "",
  idStage: null,
  idMaintenance: null,
  idService: null,
  idStatus: null,
  idScope: "",
  idSubScope: [],
  idOrigin: null,
  valueOriginal: "",
  valueDiscount: "",
  valueIncrease: "",
  idQualityAnalyst: null,
  idTeam: null,
  idOutsourced: null,
  status: "",
  occurrence: null,
  completedDate: null,
  sendDate: null,
  internalPreparationDate: null,
  plannedStartDate: null,
  actualStartDate: null,
  bookSubmissionDate: null,
  expectedSupplyCost: null,
  expectedFreightCost: null,
  expectedTeamCost: null,
  totalCost: null,
  security: null,
  externalCode: "",
  quality: null,
  stages: null,
  idCustomer: null,
  idResponsible: null,
  requestedDate: null,
  plannedDate: null,
  deadlineDate: null,
  customerApprovalDate: null,
  description: "",
  idRequester: null,
  slaEvents: [],
};

export const useActivities = ({
  filter: filterInitial = null,
  disabledShowId,
  includes = null,
  disabledGetAll,
  defaultLimit,
  idMaintenance,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  idMaintenance?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const normalizeIdSubScope = (idSubScope: any): Array<string | number> => {
    const cleanValue = (value: any) => {
      if (value === null || value === undefined) return null;

      const normalized = String(value).trim();
      if (!normalized || normalized === "[]" || normalized === "null") {
        return null;
      }

      const parsedNumber = Number(normalized);
      if (!Number.isNaN(parsedNumber) && normalized === String(parsedNumber)) {
        return parsedNumber;
      }

      return normalized;
    };

    if (Array.isArray(idSubScope)) {
      return idSubScope.map(cleanValue).filter((v) => v !== null);
    }

    if (typeof idSubScope === "string") {
      const trimmed = idSubScope.trim();

      if (!trimmed || trimmed === "[]") return [];

      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(cleanValue).filter((v) => v !== null);
          }
        } catch {
          // Mantém fluxo com fallback para valor único.
        }
      }

      const single = cleanValue(trimmed);
      return single !== null ? [single] : [];
    }

    return [];
  };

  const {
    idActivity: id,
    idMaintenance: idMaintenanceParam,
    idEntry,
  } = useParams();

  const location = useLocation();

  const [activities, setActivities] = useState<IActivity[]>([]);
  const [activity, setActivity] = useState<IActivity>(DEFAULT_DATA);
  const [sending, setSending] = useState(false);

  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial || null, defaultLimit });
  // ✅ nossa alteração: page 0-based
  const [totalItems, setTotalItems] = useState<number>(0);

  const [enableChangePositionMap, setEnableChangePositionMap] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  // Dentro de uma manutenção a atividade vive sob ela, e o voltar devolve para a manutenção
  const idMaintenanceCurrent = idMaintenanceParam || idMaintenance;
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: idMaintenanceCurrent
      ? `/towermanagement/maintenances/${idMaintenanceCurrent}/activities`
      : "/towermanagement/activities",
    pathBack: idMaintenanceCurrent
      ? `/towermanagement/maintenances/${idMaintenanceCurrent}`
      : "/towermanagement/activities",
  });

  const handleActivity = (key: string, value: any, custom?: (v: any) => any) => {
    setActivity((curr: any) => {
      const finalValue = key === "externalCode" && typeof value === "string" ? value.replace(/\s/g, "") : value;
      let newData = updateValue(curr, key, finalValue);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const CalculateValueNet = (activity: any) => {
    const valueNet =
      Number(activity.valueOriginal) +
      Number(activity.valueIncrease || 0) -
      Number(activity.valueDiscount || 0);
    handleActivity("valueNet", valueNet);
  };

  // ✅ nossa alteração: getAll trabalha com page 0-based e converte pro backend (1-based)
  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
        sort,
      }: {
        page?: number; // 0-based
        limit?: number;
        filter?: any;
        sort?: Record<string, any> | null;
      }) => {
        setLoading(true);

        listActivities({
          includes: [
            ...(includes || []),
            "qualityAnalyst",
            "service",
            "scope",
            "status",
            "outsourced",
            "team",
            "stages",
          ],
          filter,
          sort: sort || undefined, // ✅ nossa alteração
          page: page,
          limit,
        })
          .then(({ activities: list, limit, currentPage, totalItems }) => {
            setActivities(list);

            // backend volta 1-based -> converte pra 0-based no estado
            const normalizedPage = Math.max(0, Number(currentPage || 1));
            setPage(normalizedPage);

            setLimit(Number(limit));
            setTotalItems(Number(totalItems));
          })
          .finally(() => setLoading(false));
      },
      300
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const search = useEntitySearch({ list: listActivities, key: "activities", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showActivity({
      id,
      includes: ["interactions", "site", "stages", ...(includes || [])],
    })
      .then(({ activity: activitiesShow }) => {
        setActivity({
          ...activitiesShow,
          idSubScope: normalizeIdSubScope(activitiesShow?.idSubScope),
        });
      })
      .finally(() => setLoading(false));
  };

  const create = async () => {
    if (activity?.idStatus) {
      const isValid = await validateRequiredFields(activity.idStatus, activity);
      if (!isValid) return;
    }
    setLoading(true);
    createActivity({
      activity: {
        ...activity,
        externalCode: activity.externalCode ? String(activity.externalCode).replace(/\s/g, "") : activity.externalCode,
        idSubScope: normalizeIdSubScope(activity?.idSubScope),
      },
    })
      .then(({ activity: activityCreate }) => {
        toast.success(`Atividade criado com sucesso!`);
        handleActivity("occurence", null);
        goEdit(activityCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const exportXlsx = (config: any) => {
    setLoading(true);
    exportsXlsxCustom({ filter, config })
      .then(() => toast.success(`Exportação realizada com sucesso!`))
      .catch(() => toast.error("Erro na exportação personalizada"))
      .finally(() => setLoading(false));
  };

  const update = async () => {
    if (activity?.idStatus) {
      const isValid = await validateRequiredFields(activity.idStatus, activity);
      if (!isValid) return;
    }
    setLoading(true);
    updateActivity({
      id: activity.id!,
      activity: {
        ...activity,
        externalCode: activity.externalCode ? String(activity.externalCode).replace(/\s/g, "") : activity.externalCode,
        idSubScope: normalizeIdSubScope(activity?.idSubScope),
      },
    })
      .then(async ({ activity: activityUpdate }) => {
        toast.success(`Atividade atualizado com sucesso!`);
        await get({ id: activityUpdate.id });
        goEdit(activityUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  const sendMessage = async (content: string, type: string = "message") => {
    try {
      setSending(true);
      if (!activity?.id) throw new Error("Atividade sem id");

      await api.post(`/activities/${activity.id}/interactions`, {
        body: content,
        type,
      });

      await get({ id: activity.id });
      toast.success("Mensagem enviada com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar a mensagem.");
    } finally {
      setSending(false);
    }
  };


  const deleteMany = async (ids: number[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const { status, data } = await deleteActivities({ ids });
      notifyDeleteResults(
        ids.map((id) => ({ id, status, data })),
        { singular: "Atividade", plural: "atividades", gender: "f" },
      );
    } catch {
    } finally {
      setLoading(false);
      await getAll({ page: 1, limit, filter, sort }); 
    }
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort });
  }, [page, limit, filter, sort, disabledGetAll, getAll]);

  useEffect(() => {
    handleActivity("idMaintenance", idMaintenanceParam);
  }, [idMaintenanceParam]);

  useEffect(() => {
    CalculateValueNet(activity);
  }, [activity?.valueDiscount, activity?.valueIncrease, activity?.valueOriginal]);

  useEffect(() => {
    const isPage = location.pathname.includes("activities");
    if (id && isPage && !disabledShowId) {
      if (id !== "new") get({ id });
    }
  }, [id]);

  return {
    activities,
    activity,
    loading,
    sending,

    setPage,
    setLimit,
    page,
    limit,

    goEdit,
    goBack,
    goAdd,

    handleActivity,
    create,
    id,
    sendMessage,
    update,


    enableChangePositionMap,
    setEnableChangePositionMap,

    totalItems,
    search,
    handleFilter,
    handleSort, // ✅ nossa alteração

    exportXlsx,
    get,
    deleteMany,
    idEntry,
  };
};
